import type { MouseEvent, Ref } from "react";
import { useEffect, useRef } from "react";

import Typography from "@/design-system/Typography";
import Balloon from "@/design-system/balloon";
import Button from "@/design-system/button";
import UserIcon from "@/design-system/icons/user-icon.svg";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type PropsWithStyleX, colors } from "../../../styles/tokens.stylex";

export type IMenuButtons = PropsWithStyleX & {
    menusList: React.ReactNode[];
    isAuthenticated: boolean;
    isMobile: boolean;
    show: boolean;
    accountMenuTitle: string;
    onOpen: (event?: MouseEvent<HTMLElement>) => void;
    onClose: (event?: MouseEvent<HTMLElement>) => void;
    resources: unknown;
    containerRef: Ref<HTMLDivElement>;
};

export default function MenuButtons(props: IMenuButtons) {
    const mouseTimeout = useRef(0);

    useEffect(
        () => () => {
            clearTimeout(mouseTimeout.current);
        },
        [props.show],
    );

    const onMouseEnterMenu = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        clearTimeout(mouseTimeout.current);
        props.onOpen(ev);
    };

    const onMouseLeaveMenu = (ev: MouseEvent<HTMLElement>) => {
        mouseTimeout.current = window.setTimeout(() => {
            mouseTimeout.current = 0;
            props.onClose(ev);
        }, 200);
    };

    const onClick = props.show ? props.onClose : onMouseEnterMenu;

    return (
        <div {...stylex.props(styles.menuComponent, props.styleX)} ref={props.containerRef}>
            <Button
                as="button"
                styleX={styles.myAccount}
                variant="none"
                size="None"
                onClick={onClick}
                onMouseEnter={onMouseEnterMenu}
                onMouseLeave={onMouseLeaveMenu}
                icon={!props.isMobile && <img src={UserIcon} alt="Sign In" height={20} width={20} />}
            >
                <Typography textElement="p" fontStyle="BodyL" styleX={styles.text}>
                    {props.accountMenuTitle}
                </Typography>
            </Button>
            <Balloon
                tailAlign="left"
                tailPosition="top"
                isOpen={props.show}
                onMouseLeave={onMouseLeaveMenu}
                onMouseEnter={onMouseEnterMenu}
                styleX={styles.dropdown}
            >
                <div {...stylex.props(styles.items)}>{props.menusList}</div>
            </Balloon>
        </div>
    );
}

const styles = stylex.create({
    menuComponent: {
        position: "relative",
        height: "100%",
    },

    myAccount: {
        color: colors.fg_Primary,
        fontWeight: 300,
        position: "relative",
        height: "100%",
        paddingRight: 0,
    },

    text: {
        color: colors.fg_Primary,
        fontWeight: 300,
    },

    dropdown: {
        left: "-50%",
    },

    items: {
        gap: ComposableSpacing.s3,
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        width: "185px",
    },
});
